import { useState } from "react";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { ExternalLink, CreditCard, FileText, MapPin, Receipt } from "lucide-react";
import { useSubscription } from "@/contexts/SubscriptionContext";
import { toast } from "@/hooks/use-toast";
import { Skeleton } from "@/components/ui/skeleton";
import { openExternalUrl } from "@/utils/nativePlugins";

const BillingTab = () => {
  const { openCustomerPortal, subscriptionStatus, subscriptionEnd, isLoading } = useSubscription();
  const [isOpeningPortal, setIsOpeningPortal] = useState(false);

  const handleOpenPortal = async () => {
    setIsOpeningPortal(true);
    try {
      const url = await openCustomerPortal();
      await openExternalUrl(url);
      toast({
        title: "Opening Customer Portal",
        description: "The Stripe Customer Portal will open in a new tab.",
      });
    } catch (error: any) {
      toast({
        title: "Error",
        description: error?.message?.startsWith("No billing account")
          ? error.message
          : "Failed to open customer portal. Please try again.",
        variant: "destructive",
      });
    } finally {
      setIsOpeningPortal(false);
    }
  };

  if (isLoading) {
    return (
      <Card>
        <CardHeader>
          <Skeleton className="h-6 w-48" />
          <Skeleton className="h-4 w-96" />
        </CardHeader>
        <CardContent>
          <Skeleton className="h-10 w-full" />
        </CardContent>
      </Card>
    );
  }

  if (subscriptionStatus === 'sponsored') {
    return (
      <Card>
        <CardHeader>
          <CardTitle>Billing & Payment Management</CardTitle>
          <CardDescription>
            Your access is complimentary and managed by an administrator. No billing information is required.
          </CardDescription>
        </CardHeader>
      </Card>
    );
  }

  return (
    <Card>
      <CardHeader>
        <CardTitle>Billing & Payment Management</CardTitle>
        <CardDescription>
          Manage your payment methods, view invoices, and update billing information through Stripe's secure portal.
        </CardDescription>
      </CardHeader>
      <CardContent className="space-y-6">
        <div className="space-y-4">
          {subscriptionStatus && (
            <div className="p-4 bg-muted/50 rounded-lg space-y-2">
              <div className="flex items-center justify-between">
                <span className="text-sm text-muted-foreground">Current Status:</span>
                <span className="text-sm font-medium capitalize">{subscriptionStatus}</span>
              </div>
              {subscriptionEnd && subscriptionStatus === 'active' && (
                <div className="flex items-center justify-between">
                  <span className="text-sm text-muted-foreground">Next Billing Date:</span>
                  <span className="text-sm font-medium">
                    {new Date(subscriptionEnd).toLocaleDateString('en-US', {
                      year: 'numeric',
                      month: 'long',
                      day: 'numeric'
                    })}
                  </span>
                </div>
              )}
            </div>
          )}

          <Button 
            onClick={handleOpenPortal}
            disabled={isOpeningPortal}
            className="w-full"
            size="lg"
          >
            {isOpeningPortal ? (
              "Opening Portal..."
            ) : (
              <>
                Open Customer Portal
                <ExternalLink className="ml-2 h-4 w-4" />
              </>
            )}
          </Button>
        </div>

        <div className="space-y-3">
          <h3 className="text-sm font-semibold">What you can do in the portal:</h3>
          <ul className="space-y-2">
            <li className="flex items-start gap-3 text-sm text-muted-foreground">
              <CreditCard className="h-4 w-4 mt-0.5 text-primary shrink-0" />
              <span>Update payment methods and card information</span>
            </li>
            <li className="flex items-start gap-3 text-sm text-muted-foreground">
              <Receipt className="h-4 w-4 mt-0.5 text-primary shrink-0" />
              <span>View complete billing history and past charges</span>
            </li>
            <li className="flex items-start gap-3 text-sm text-muted-foreground">
              <FileText className="h-4 w-4 mt-0.5 text-primary shrink-0" />
              <span>Download invoices and receipts for your records</span>
            </li>
            <li className="flex items-start gap-3 text-sm text-muted-foreground">
              <MapPin className="h-4 w-4 mt-0.5 text-primary shrink-0" />
              <span>Update billing address and contact information</span>
            </li>
          </ul>
        </div>

        <div className="pt-4 border-t">
          <p className="text-xs text-muted-foreground">
            All payment and billing operations are securely handled by Stripe, our payment processor.
          </p>
        </div>
      </CardContent>
    </Card>
  );
};

export default BillingTab;
